import styled from '@emotion/styled';
import { CncReport, Project } from '@gameonly/core';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import { CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../authentication/authenticatedFetch';
import useReports from '../cnc-reports/context/useReports';
import { IconContainer } from '../results/ResultsSimple';
import Button, { WhiteButton } from '../ui/Button';
import { ReactComponent as Info } from '../ui/icons/information.svg';
import CreateReportModal from './CreateReport';
import { downloadFile } from './ProjectsUtils';

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CNCProjectSection = ({ project }: { project: Project }) => {
  const { t } = useTranslation(['project', 'common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { reports } = useReports({ projectId: project._id });

  const handleNewReport = () => {
    setOpenCreate(true);
  };

  const handleExportForPartner = async () => {
    setLoading(true);
    const params = { exportType: 'local' };
    const queryParams = new URLSearchParams(params).toString();

    const response = await authenticatedFetch(
      `${process.env.REACT_APP_API_URL}/projects/${project._id}?${queryParams}`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      const error = await response.json();
      setLoading(false);
      enqueueSnackbar(t(`project:export.error.${error.message}`), {
        variant: 'error',
      });
      return;
    }

    const exportBlob = await response.blob();
    const filename = `project_${project._id}_${new Date().toISOString()}.json`;
    downloadFile(exportBlob, filename);
    setLoading(false);
  };

  const isProrata = !!project.prorataInformation;
  const isValidated = !!project.validatedAt;

  return (
    <>
      {openCreate && (
        <CreateReportModal
          project={project}
          open={openCreate}
          onClose={() => setOpenCreate(false)}
        />
      )}
      <Title>
        <p className="h4b">{t('project:cnc-section.title')}</p>
        {reports && reports?.length === 1 && (
          <WhiteButton
            onClick={() => navigate(`/cnc-reports/${reports![0]._id}`)}
          >
            {t('common:open')}
          </WhiteButton>
        )}
        {!isProrata && !isValidated && reports?.length === 0 && (
          <Tooltip
            title={t('project:cnc-section.information-tooltip')}
            arrow
            sx={{ cursor: 'pointer' }}
          >
            <Info style={{ width: '32px', height: '32px' }} />
          </Tooltip>
        )}
      </Title>
      {reports && reports?.length > 1 && (
        <ReportList project={project} reports={reports} />
      )}
      {isValidated && (
        <>
          <Tooltip title={t('project:cnc-section.export-button-tooltip')}>
            <WhiteButton
              onClick={handleExportForPartner}
              disabled={project.completionRate !== 100}
            >
              <IconContainer>
                <DownloadForOfflineOutlinedIcon />
              </IconContainer>
              {t('project:cnc-section.export-button')}
              {loading && (
                <CircularProgress sx={{ maxWidth: 20, maxHeight: 20 }} />
              )}
            </WhiteButton>
          </Tooltip>
          {!isProrata && (
            <Button
              onClick={handleNewReport}
              color="primary"
              disabled={project.completionRate !== 100}
            >
              {t('project:cnc-section.new-report-button')}
            </Button>
          )}
        </>
      )}
    </>
  );
};

const List = styled.div`
  list-style: none;
  padding-left: 0;
`;

const ReportList = ({
  reports,
  project,
}: {
  reports: CncReport[] | undefined;
  project: Project;
}) => {
  const { t } = useTranslation(['project', 'common']);
  if (!reports) {
    return (
      <div
        className="loader-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '20px',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ maxWidth: 20, maxHeight: 20 }} />
      </div>
    );
  }
  if (reports.length === 0) return null;
  return (
    <List>
      <p className="hxr">
        {t('project:cnc-section.report-list-title', {
          modelSingularName: project.model.singularName.toLocaleLowerCase(),
        })}
      </p>
      <ul className="custom-list">
        {reports.map((report) => (
          <li key={report._id.toString()}>
            <a
              href={`/cnc-reports/${report._id}`}
              rel="noopener noreferrer"
              className="hxr underline"
            >
              {report.name} - {new Date(report.createdAt).toLocaleDateString()}
            </a>
          </li>
        ))}
      </ul>
    </List>
  );
};

export default CNCProjectSection;
